<template>
  <PForm nested :schema="r$" :validate-on="[]" class="availability-form-base">
    <div class="space-y-2">
      <div class="grid grid-cols-12 gap-x-2">
        <div class="col-span-6 grid grid-cols-12 gap-x-2">
          <PFormField label="Vanaf" class="col-span-8">
            <PInputDate
              v-model="startDate"
              :teleport="false"
              placeholder="DD-MM-YY"
              :disabled="disabled"
              @update:model-value="save"
            />
          </PFormField>
          <PFormField label="Starttijd" sr-only class="col-span-4">
            <PInputTime
              v-model="startTime"
              :disabled="disabled || !startDate"
              @focusout="onFocusOutTimeStart"
              ref="startTimePicker"
            />
          </PFormField>
        </div>
        <div class="col-span-6 grid grid-cols-12 gap-x-2">
          <PFormField label="Tot en met" class="col-span-8">
            <PInputDate
              v-model="endDate"
              :teleport="false"
              placeholder="DD-MM-YY"
              :disabled="disabled || !startDate"
              @update:model-value="save"
            />
          </PFormField>
          <PFormField label="Eindtijd" sr-only class="col-span-4">
            <PInputTime
              ref="endTimePicker"
              v-model="endTime"
              :disabled="disabled || !endDate"
              @focusout="onFocusOutTimeEnd"
            />
          </PFormField>
        </div>
        <div v-if="r$.range.$silentErrors.length > 0" class="col-span-12">
          <span class="text-error text-sm select-none">
            {{ r$.range.$silentErrors[0] }}
          </span>
        </div>
      </div>

      <PFormField label="Opmerking">
        <PTextarea
          v-model="availabilityBlock.description!"
          placeholder="Aanvullende informatie over inzetbaarheid"
          :disabled="disabled"
          autoresize
          class="w-full"
          @focusout="save"
        />
      </PFormField>
    </div>
  </PForm>
</template>

<script setup lang="ts">
import PForm from "@/components/form/PForm.vue"
import { computed, inject, ref } from "vue"
import PFormField from "@/components/form/PFormField.vue"
import type { Moment } from "moment"
import { useRegle, markStatic } from "@regle/core"
import PInputDate from "@/components/form/PInputDate.vue"
import PTextarea from "@/components/form/PTextarea.vue"
import PInputTime from "@/components/form/PInputTime.vue"
import type { AvailabilityBucket } from "@/models/availability-bucket.model.ts"
import type { AvailabilityBlockController } from "@/controllers/model-controllers/availability-block-controller.ts"
import type { AvailabilityBlock } from "@/models/availability-block.model.ts"
import {
  getDatePortion,
  getTimePortion,
  isMidnight,
  setDatePortion,
  setTimePortion
} from "@/helper/time-helper.ts"
import { chronologicalStartAndEndTimeValidator } from "@/helper/validation/time-validation.ts"

const props = withDefaults(
  defineProps<{
    availabilityBucket: AvailabilityBucket
    disabled?: boolean
  }>(),
  {
    disabled: false
  }
)

const availabilityBlockController = inject<AvailabilityBlockController>(
  "availabilityBlockController"
)!

const availabilityBlock = ref<AvailabilityBlock>(
  props.availabilityBucket.getBaseAvailabilityBlock()
)

// Split timeStart into separate date and time computed refs for the input controls.
const startDate = computed<Moment | null>({
  get: () => getDatePortion(availabilityBlock.value.timeStart),
  set: (value) =>
    (availabilityBlock.value.timeStart = setDatePortion(availabilityBlock.value.timeStart, value))
})

const startTime = computed<Moment | null>({
  get: () => getTimePortion(availabilityBlock.value.timeStart),
  set: (value) =>
    (availabilityBlock.value.timeStart = setTimePortion(availabilityBlock.value.timeStart, value))
})

// Split timeEnd into separate date and time computed refs for the input controls.
const endDate = computed<Moment | null>({
  get: () => getDatePortion(availabilityBlock.value.timeEnd),
  set: (value) =>
    (availabilityBlock.value.timeEnd = setDatePortion(availabilityBlock.value.timeEnd, value))
})

const endTime = computed<Moment | null>({
  get: () => getTimePortion(availabilityBlock.value.timeEnd),
  set: (value) =>
    (availabilityBlock.value.timeEnd = setTimePortion(availabilityBlock.value.timeEnd, value))
})

// Clear the time picker if the time is midnight
const startTimePicker = ref<InstanceType<typeof PInputTime>>()
function onFocusOutTimeStart() {
  if (isMidnight(availabilityBlock.value.timeStart)) {
    startTimePicker.value?.clear()
  }
  save()
}

// Clear the time picker if the time is midnight
const endTimePicker = ref<InstanceType<typeof PInputTime>>()
function onFocusOutTimeEnd() {
  if (isMidnight(availabilityBlock.value.timeEnd)) {
    endTimePicker.value?.clear()
  }
  save()
}

async function save() {
  const { valid: isValid } = await r$.$validate()
  if (!isValid) {
    return
  }

  await availabilityBlockController.update(availabilityBlock.value)
}

// VALIDATION RULES
const validationRules = {
  range: {
    timeRange: chronologicalStartAndEndTimeValidator({
      allowEqual: true,
      treatMidnightAsEndOfDay: true
    })
  }
}

const validationState = computed(() => ({
  range: markStatic({
    timeStart: availabilityBlock.value.timeStart,
    timeEnd: availabilityBlock.value.timeEnd
  })
}))

const { r$ } = useRegle(validationState, validationRules, { autoDirty: false })
</script>

<style scoped></style>
